/**
 * Fit › Videos — curación de la URL de video de cada ejercicio del catálogo.
 * Buscá / filtrá, pegá el link directo (YouTube u otro) y guardá inline. Sin
 * curar, la ficha de `apps/fit` cae a la búsqueda armada de YouTube.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchFitExercises,
  setFitCuratedVideo,
  type AdminFitExercise,
} from '../../api/admin/fit.js';
import { AdminApiError } from '../../api/admin/_core.js';

const PAGE_SIZE = 30;
type CuratedFilter = '' | 'yes' | 'no';

export function FitVideosPage(): ReactElement {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [curated, setCurated] = useState<CuratedFilter>('');
  const [page, setPage] = useState(1);

  // Debounce del buscador (evita un fetch por tecla).
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(id);
  }, [q]);
  // Cualquier cambio de filtro vuelve a la página 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, curated]);

  const query = useQuery({
    queryKey: ['admin', 'fit', 'exercises', { q: debouncedQ, curated, page }],
    queryFn: () =>
      fetchFitExercises({
        ...(debouncedQ ? { q: debouncedQ } : {}),
        ...(curated ? { curated } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Fit · Videos</h1>
          <p className="page-sub">Curá el video de cada ejercicio · fallback a búsqueda YouTube</p>
        </div>
      </div>

      <section className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={fieldStyle}>
          Buscar
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre del ejercicio (ES o EN)…"
            style={{ minWidth: 260 }}
          />
        </label>
        <label style={fieldStyle}>
          Curación
          <select className="input" value={curated} onChange={(e) => setCurated(e.target.value as CuratedFilter)}>
            <option value="">Todos</option>
            <option value="yes">Con video curado</option>
            <option value="no">Sin curar</option>
          </select>
        </label>
        <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)', fontSize: 13 }}>
          {query.isPending ? 'Cargando…' : `${total} ejercicios`}
        </span>
      </section>

      {query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}></th>
                <th>Ejercicio</th>
                <th>Grupo</th>
                <th>Equipo</th>
                <th>Video curado</th>
              </tr>
            </thead>
            <tbody>
              {query.data && query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty">
                    Sin resultados con esos filtros.
                  </td>
                </tr>
              ) : (
                (query.data?.items ?? []).map((ex) => <ExerciseRow key={ex.id} ex={ex} />)
              )}
            </tbody>
          </table>
        </section>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
          <button type="button" className="btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Anterior
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            Página {page} de {totalPages}
          </span>
          <button type="button" className="btn ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente →
          </button>
        </div>
      )}
    </>
  );
}

function ExerciseRow({ ex }: { ex: AdminFitExercise }): ReactElement {
  const qc = useQueryClient();
  const [url, setUrl] = useState(ex.curatedVideoUrl ?? '');
  // Re-sincroniza si el server devuelve otro valor (ej. tras guardar / cambiar de página).
  useEffect(() => {
    setUrl(ex.curatedVideoUrl ?? '');
  }, [ex.curatedVideoUrl]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'fit', 'exercises'] });
  };
  const save = useMutation({
    mutationFn: (next: string | null) => setFitCuratedVideo(ex.id, next),
    onSuccess: invalidate,
  });

  const trimmed = url.trim();
  const dirty = trimmed !== (ex.curatedVideoUrl ?? '');

  return (
    <tr>
      <td>
        {ex.gifUrl ? (
          <img src={ex.gifUrl} alt="" width={48} height={48} style={{ borderRadius: 6, objectFit: 'cover', background: 'var(--surface-2, #1a1a1a)' }} loading="lazy" />
        ) : (
          <div style={{ width: 48, height: 48, borderRadius: 6, background: 'var(--surface-2, #1a1a1a)' }} />
        )}
      </td>
      <td>
        <div style={{ fontWeight: 600 }}>{ex.nameEs ?? ex.nameEn}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{ex.nameEn}</div>
      </td>
      <td>
        <code>{ex.target}</code>
      </td>
      <td>{ex.equipment}</td>
      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=…"
            style={{ minWidth: 280, flex: 1 }}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || save.isPending || (trimmed !== '' && !/^https?:\/\//i.test(trimmed))}
            onClick={() => save.mutate(trimmed === '' ? null : trimmed)}
          >
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          {ex.curatedVideoUrl && (
            <button
              type="button"
              className="btn ghost"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
              title="Quitar el video curado (vuelve al fallback de YouTube)"
            >
              Limpiar
            </button>
          )}
          {ex.curatedVideoUrl && <span className="pill pill-good">curado</span>}
        </div>
        {save.isError && (
          <p style={{ color: 'var(--error)', margin: '4px 0 0', fontSize: 12 }}>
            {save.error instanceof AdminApiError ? save.error.message : String(save.error)}
          </p>
        )}
      </td>
    </tr>
  );
}

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-tertiary)',
};
